import React, { useState, useEffect, useContext } from 'react';
import Icon from './AppIcon';
import creditService from '../utils/creditService';
import AuthContext from '../contexts/AuthContext';

const CreditUsageNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const [isManuallyHidden, setIsManuallyHidden] = useState(false);
  const [lastKnownCredits, setLastKnownCredits] = useState(null);
  const { user, refreshUserCredits } = useContext(AuthContext);

  const checkCreditUsage = async (forceShow = false) => {
    try {
      const stats = await creditService.getCreditStats();
      
      if (stats && !stats.isUnlimited && stats.isActive) {
        // Check if credits were consumed (decreased)
        const creditsConsumed = lastKnownCredits && stats.remaining < lastKnownCredits.remaining;
        
        // Reset manual hide state if credits were consumed during validation
        if (creditsConsumed && isManuallyHidden) {
          // console.log('Credits consumed during validation - resetting manual hide state');
          setIsManuallyHidden(false);
        }
        
        // Update last known credits
        setLastKnownCredits(stats);
        // console.log(`Current credits: ${stats.remaining} remaining, ${stats.usagePercentage}% used`);
        
        // Check if user has used 70% or more of their credits
        const shouldShow = stats.usagePercentage >= 70;
        
        if (shouldShow) {
          setCreditInfo(stats);
          // Only show if not manually hidden (unless forced)
          const willShow = !isManuallyHidden || forceShow;
          // console.log(`Credit usage ${stats.usagePercentage}% - Notification ${willShow ? 'SHOWING' : 'HIDDEN (manually closed)'}`);
          setIsVisible(willShow);
        } else {
          // console.log(`Credit usage ${stats.usagePercentage}% - Notification hidden (below 70% threshold)`);
          setIsVisible(false);
          setIsManuallyHidden(false); // Reset manual hide when credits are below threshold
        }
      } else {
        setIsVisible(false);
        setIsManuallyHidden(false); // Reset manual hide when no active plan
      }
    } catch (error) {
      // console.error('Error checking credit usage:', error);
      setIsVisible(false);
      setIsManuallyHidden(false); // Reset manual hide on error
    }
  };

  useEffect(() => {
    // Initial check
    checkCreditUsage();
    
    // Check every 30 seconds for credit updates, but only if not manually hidden
    const interval = setInterval(() => {
      if (!isManuallyHidden) {
        checkCreditUsage();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [user, isManuallyHidden]);

  // Listen for credit updates from AuthContext
  useEffect(() => {
    if (user && !isManuallyHidden) {
      // Check credits immediately when user changes, but only if not manually hidden
      checkCreditUsage();
    }
  }, [user, isManuallyHidden]);

  // Listen for custom credit update events
  useEffect(() => {
    const handleCreditUpdate = () => {
      // console.log('Credit update event received, checking for credit consumption...');
      // Check if credits were actually consumed (not just updated)
      // Add a small delay to ensure credits are updated in the database
      setTimeout(() => checkCreditUsage(), 500);
    };

    const handleStorageChange = (e) => {
      if (e.key === 'userCredits') {
        // console.log('LocalStorage credit change detected, checking for credit consumption...');
        // Check if credits were actually consumed
        setTimeout(() => checkCreditUsage(), 100);
      }
    };

    // Listen for custom credit update events
    window.addEventListener('creditsUpdated', handleCreditUpdate);
    
    // Listen for localStorage changes as backup
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('creditsUpdated', handleCreditUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Add/remove body padding when notification is visible
  useEffect(() => {
    if (isVisible) {
      document.body.style.paddingTop = '60px';
    } else {
      document.body.style.paddingTop = '0px';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.paddingTop = '0px';
    };
  }, [isVisible]);

  const handleUpgrade = () => {
    // Navigate to pricing page
    window.location.href = '/pricing';
  };

  const handleClose = () => {
    // console.log('Notification manually closed by user');
    setIsManuallyHidden(true);
    setIsVisible(false);
  };

  if (!isVisible || !creditInfo) {
    return null;
  }

  const getNotificationType = () => {
    if (creditInfo.usagePercentage >= 100) {
      return {
        type: 'exhausted',
        icon: 'AlertTriangle',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-300',
        title: 'Credits Exhausted!',
        message: `You've used all your credits! Upgrade now to continue validating emails.`
      };
    } else if (creditInfo.usagePercentage >= 90) {
      return {
        type: 'critical',
        icon: 'AlertTriangle',
        color: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        title: 'Critical Credit Usage!',
        message: `Running low on credits! Only ${creditService.formatCreditsDisplay(creditInfo.remaining)} remaining.`
      };
    } else if (creditInfo.usagePercentage >= 80) {
      return {
        type: 'warning',
        icon: 'AlertCircle',
        color: 'text-orange-500',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        title: 'High Credit Usage',
        message: `You're using ${creditInfo.usagePercentage}% of your credits. Consider upgrading for more validations.`
      };
    } else {
      return {
        type: 'info',
        icon: 'Info',
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        title: 'Credit Usage Alert',
        message: `A friendly reminder: You've used ${creditInfo.usagePercentage}% of your credits.`
      };
    }
  };

  const notification = getNotificationType();

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${isVisible ? 'animate-slide-in-top' : 'hidden'}`}>
      <div className={`${notification.bgColor} ${notification.borderColor} border-b shadow-lg px-4 py-3 relative backdrop-blur-sm ${notification.type === 'exhausted' ? 'animate-pulse-glow border-red-400' : notification.type === 'critical' ? 'animate-pulse-glow' : ''}`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-repeat-x" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23${notification.type === 'exhausted' ? 'dc2626' : notification.type === 'critical' ? 'dc2626' : notification.type === 'warning' ? 'd97706' : '3b82f6'}' fill-opacity='0.1'%3E%3Cpath d='M10 0L20 10L10 20L0 10Z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>

        <div className="relative flex items-center justify-between max-w-7xl mx-auto">
          {/* Left section - Icons and message */}
          <div className="flex items-center space-x-3">
            {/* Sparkle icon */}
            <div className={`${notification.color} flex-shrink-0`}>
              <Icon name="Sparkles" size={16} />
            </div>
            
            {/* Main icon */}
            <div className={`${notification.color} flex-shrink-0`}>
              <Icon name={notification.icon} size={18} />
            </div>
            
            {/* Message */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {notification.message}
              </p>
               <div className="text-xs text-gray-600 mt-0.5">
                 {notification.type === 'exhausted' ? 'No credits remaining' : `${creditService.formatCreditsDisplay(creditInfo.remaining)} credits remaining`} • {creditService.getPlanDisplayName(creditInfo.plan, creditInfo.billingCycle, creditInfo.isUnlimited)}
               </div>
            </div>
          </div>

          {/* Center section - Progress bar */}
          <div className="hidden md:flex items-center mx-6">
            <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
               <div 
                 className={`h-2 rounded-full transition-all duration-500 ${
                   notification.type === 'exhausted' ? 'bg-red-600' :
                   notification.type === 'critical' ? 'bg-red-500' :
                   notification.type === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
                 }`}
                 style={{ width: `${Math.min(creditInfo.usagePercentage, 100)}%` }}
               ></div>
            </div>
            <span className="text-xs font-medium text-gray-700">{creditInfo.usagePercentage}%</span>
          </div>

          {/* Right section - Action button */}
          <div className="flex items-center space-x-3">
            {/* Upgrade button */}
             <button
               onClick={handleUpgrade}
               className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all duration-200 hover:shadow-md ${
                 notification.type === 'exhausted' ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 animate-pulse' :
                 notification.type === 'critical' ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' :
                 notification.type === 'warning' ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700' : 
                 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
               }`}
             >
               <div className="flex items-center space-x-2">
                 <Icon name="ArrowUp" size={14} />
                 <span>{notification.type === 'exhausted' ? 'Upgrade Now!' : 'Upgrade Plan'}</span>
               </div>
            </button>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/70 rounded-full transition-all duration-200 hover:scale-110 bg-white/30"
              aria-label="Close notification"
            >
              <Icon name="X" size={16} className="text-gray-600 hover:text-gray-800" />
            </button>
          </div>
        </div>

         {/* Bottom accent line */}
         <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${
           notification.type === 'exhausted' ? 'bg-red-600' :
           notification.type === 'critical' ? 'bg-red-500' :
           notification.type === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
         }`}></div>
      </div>
    </div>
  );
};

export default CreditUsageNotification;
